import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { availableAppContexts, schemeMap } from '../common/charity'
import { filterCharities, getFilterFromSearchParams } from '../../../../lib/charity'
import { CharityFilter } from './common/filter/CharityFilter'
import { SelectAppContext } from './common/SelectAppContext'
import { GenerateCSVReport } from '../../../common/GenerateCharityReport'
import { getCharityReportData } from '../../../../services/charity-report.service'
import { CharityReportTable } from './common/CharityReportTable'

let abortRequests: () => void

export const CharityReportPage = () => {
  const [requestStatus, setRequestStatus] = useState<RequestStatusType | 'loaded'>('idle')
  const [charities, setCharities] = useState<CharityReportType[]>([])
  const [filteredCharities, setFilteredCharities] = useState<CharityReportType[]>([])
  const [error, setError] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [appContext, setAppContext] = useState<CharityAppContext>(searchParams.get('appContext') as CharityAppContext)
  const [noteFilterValues, setNoteFilterValues] = useState<string[]>([])

  const appContextSearchParam = useMemo(() => searchParams.get('appContext') as CharityAppContext, [searchParams])

  function fetchData() {
    if (!availableAppContexts.includes(appContext)) {
      setError('Invalid app')
      return
    }

    if (charities.length > 0) {
      return
    }

    if (requestStatus === 'pending') {
      return
    }

    setError('')
    setRequestStatus('pending')

    const { abort, run } = getCharityReportData(appContext)

    abortRequests = abort

    run()
      .then(data => {
        setCharities(data)

        const notes = [
          ...new Set(data.map((c: CharityReportType) => c.charity.note).filter((note: string) => note))
        ] as string[]
        setNoteFilterValues(notes)

        setRequestStatus('loaded')
      })
      .catch(error => {
        if (error?.name !== 'AbortError') {
          setRequestStatus('failure')
          setError('Something went wrong. Please try again later.')
        }
      })
  }

  function updateSearchParams(filter: CharityFilterType) {
    if (!filter.appContext) {
      return
    }

    const newParams = new URLSearchParams()

    if (filter.appContext !== appContext) {
      setCharities([])
      setFilteredCharities([])
      setAppContext(filter.appContext)
    }

    Object.keys(filter).forEach(key => {
      const value = filter[key as keyof CharityFilterType]

      if (Array.isArray(value)) {
        if (value.length > 0) {
          newParams.set(key, value.toString())
        }
      } else if (value) {
        newParams.set(key, value)
      }
    })

    setSearchParams(newParams)
  }

  useEffect(() => {
    if (appContext) {
      fetchData()
    }

    return () => {
      if (abortRequests) {
        abortRequests()
      }
    }
  }, [appContext])

  useEffect(() => {
    if (requestStatus === 'loaded') {
      const filter = getFilterFromSearchParams(searchParams)
      setFilteredCharities(filterCharities(charities, filter))
    }
  }, [requestStatus])

  useEffect(() => {
    const filter = getFilterFromSearchParams(searchParams)

    if (charities.length > 0) {
      if (Object.keys(filter).filter(k => k !== 'appContext')?.length > 0) {
        setFilteredCharities(filterCharities(charities, filter))
      } else {
        setFilteredCharities(charities)
      }
    }
  }, [searchParams])

  if (!appContextSearchParam) {
    return (
      <div className="mx-auto max-w-screen-2xl py-md grid gap-lg relative">
        <h1 className="text-xl">Select app</h1>
        <SelectAppContext style="button" onSelected={updateSearchParams} />
      </div>
    )
  }

  return (
    <>
      <div className="mx-auto max-w-screen-2xl py-md grid gap-lg">
        <div className="flex justify-between items-end">
          <h1 className="text-xl">Charities in {schemeMap[appContextSearchParam]}</h1>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="grid grid-cols-12 gap-md">
          <div className="col-span-3">
            <CharityFilter
              appContext={appContextSearchParam}
              noteFilterValues={noteFilterValues}
              onFilterChanged={updateSearchParams}
            />
          </div>

          <div className="col-span-9 card relative">
            {requestStatus === 'pending' && (
              <div className="absolute left-1/2 -translate-x-1/2 top-[20rem] flex flex-col space-y-md p-lg items-center justify-center bg-white rounded-half z-[1] shadow-lg">
                <div className="loadingSpinner" />
                <p>Loading data. This might take a while...</p>
              </div>
            )}

            <div className="p-md flex justify-between">
              <div>
                Showing {filteredCharities?.length}{' '}
                {searchParams.get('status')?.toLowerCase()?.split(',').join(' and ')}{' '}
                {charities?.length > 1 ? 'charities' : 'charity'} out of {charities?.length} total
              </div>

              <GenerateCSVReport filePrefix={searchParams.get('appContext') as string} data={filteredCharities} />
            </div>

            {requestStatus !== 'pending' && <CharityReportTable data={filteredCharities} />}
          </div>
        </div>
      </div>
    </>
  )
}
